import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import {
  Alert,
  Button,
  Col,
  Form,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap"

import ButtonMain from "components/Common/Button"
import AvailabilityPicker from "./AvailabilityPicker"

const EMPTY_TIMING = {
  date: "",
  startTime: "09:00",
  endTime: "18:00",
  lunchStart: "13:00",
  lunchEnd: "14:00",
}

export const validateTiming = (values, requireDate = true) => {
  const errors = {}
  if (requireDate && !values.date) errors.date = "Please select a date."
  if (!values.startTime) errors.startTime = "Start time is required."
  if (!values.endTime) errors.endTime = "End time is required."
  if (!values.lunchStart) errors.lunchStart = "Lunch start is required."
  if (!values.lunchEnd) errors.lunchEnd = "Lunch end is required."

  if (
    values.startTime &&
    values.endTime &&
    values.startTime >= values.endTime
  ) {
    errors.endTime = "End time must be after start time."
  }
  if (
    values.lunchStart &&
    values.lunchEnd &&
    values.lunchStart >= values.lunchEnd
  ) {
    errors.lunchEnd = "Lunch end must be after lunch start."
  }
  if (
    values.startTime &&
    values.endTime &&
    values.lunchStart &&
    values.lunchEnd &&
    (values.lunchStart < values.startTime || values.lunchEnd > values.endTime)
  ) {
    errors.lunchRange = "Lunch must be within working hours."
  }
  return errors
}

const AvailabilityTimingModal = ({
  isOpen,
  initialValue,
  isSaving,
  apiError,
  onClose,
  onSave,
}) => {
  const [values, setValues] = useState(EMPTY_TIMING)
  const [errors, setErrors] = useState({})

  useEffect(() => {
    setValues(
      initialValue ? { ...EMPTY_TIMING, ...initialValue } : EMPTY_TIMING
    )
    setErrors({})
  }, [initialValue, isOpen])

  const updateValue = event => {
    const { name, value } = event.target
    setValues(current => ({ ...current, [name]: value }))
    setErrors(current => ({ ...current, [name]: "", lunchRange: "" }))
  }

  const submit = event => {
    event.preventDefault()
    const nextErrors = validateTiming(values)
    setErrors(nextErrors)
    if (!Object.keys(nextErrors).length) onSave(values)
  }

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
      <Form onSubmit={submit}>
        <ModalHeader toggle={onClose}>
          {initialValue ? "Edit Particular Day" : "Add Particular Day"}
        </ModalHeader>
        <ModalBody>
          {apiError && <Alert color="danger">{apiError}</Alert>}
          <Row>
            <Col md={6} className="mb-3">
              <Label for="availability-date">Date</Label>
              <AvailabilityPicker
                id="availability-date"
                name="date"
                type="date"
                value={values.date}
                onChange={updateValue}
                invalid={!!errors.date}
                disabled={!!initialValue}
              />
              {errors.date && (
                <div className="invalid-feedback d-block">{errors.date}</div>
              )}
            </Col>
          </Row>
          <Row>
            <Col md={6} className="mb-3">
              <Label for="particular-start">Working Start</Label>
              <AvailabilityPicker
                id="particular-start"
                name="startTime"
                type="time"
                value={values.startTime}
                onChange={updateValue}
                invalid={!!errors.startTime}
              />
              {errors.startTime && (
                <div className="invalid-feedback d-block">
                  {errors.startTime}
                </div>
              )}
            </Col>
            <Col md={6} className="mb-3">
              <Label for="particular-end">Working End</Label>
              <AvailabilityPicker
                id="particular-end"
                name="endTime"
                type="time"
                value={values.endTime}
                onChange={updateValue}
                invalid={!!errors.endTime}
              />
              {errors.endTime && (
                <div className="invalid-feedback d-block">{errors.endTime}</div>
              )}
            </Col>
            <Col md={6} className="mb-3">
              <Label for="particular-lunch-start">Lunch Start</Label>
              <AvailabilityPicker
                id="particular-lunch-start"
                name="lunchStart"
                type="time"
                value={values.lunchStart}
                onChange={updateValue}
                invalid={!!errors.lunchStart || !!errors.lunchRange}
              />
              {(errors.lunchStart || errors.lunchRange) && (
                <div className="invalid-feedback d-block">
                  {errors.lunchStart || errors.lunchRange}
                </div>
              )}
            </Col>
            <Col md={6} className="mb-3">
              <Label for="particular-lunch-end">Lunch End</Label>
              <AvailabilityPicker
                id="particular-lunch-end"
                name="lunchEnd"
                type="time"
                value={values.lunchEnd}
                onChange={updateValue}
                invalid={!!errors.lunchEnd || !!errors.lunchRange}
              />
              {(errors.lunchEnd || errors.lunchRange) && (
                <div className="invalid-feedback d-block">
                  {errors.lunchEnd || errors.lunchRange}
                </div>
              )}
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            type="button"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <ButtonMain
            className="btn btn-secondary"
            type="submit"
            isLoading={isSaving}
            disabled={isSaving}
          >
            Save Day
          </ButtonMain>
        </ModalFooter>
      </Form>
    </Modal>
  )
}

AvailabilityTimingModal.propTypes = {
  apiError: PropTypes.string,
  initialValue: PropTypes.object,
  isOpen: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
}

export default AvailabilityTimingModal
